import type { Sentence, EnhancedMetaRaw, Topic } from './types.js';

interface EnhancedMeta {
  sentences: Sentence[];
  topic: Topic | null;
}

const cache = new Map<string, EnhancedMeta>();
const inflight = new Map<string, Promise<EnhancedMeta>>();

export async function loadEnhancedMeta(metaUrl: string): Promise<EnhancedMeta> {
  const cached = cache.get(metaUrl);
  if (cached) return cached;
  const p = inflight.get(metaUrl);
  if (p) return p;
  const req = (async () => {
    try {
      const resp = await fetch(metaUrl, { cache: 'force-cache' });
      if (!resp.ok) throw new Error(`meta HTTP ${resp.status} for ${metaUrl}`);
      const raw = (await resp.json()) as EnhancedMetaRaw;
      const normalized = normalize(raw);
      const topic = extractTopic(raw);
      const result: EnhancedMeta = { sentences: normalized, topic };
      cache.set(metaUrl, result);
      return result;
    } finally {
      // A failed request must not poison this URL for the rest of the page
      // lifetime; callers can retry after a transient NAS/network failure.
      inflight.delete(metaUrl);
    }
  })();
  inflight.set(metaUrl, req);
  return req;
}

/**
 * Backward-compatible loader returning only the sentence array.
 * Prefer {@link loadEnhancedMeta} when topic access is needed.
 */
export async function loadSentences(metaUrl: string): Promise<Sentence[]> {
  const meta = await loadEnhancedMeta(metaUrl);
  return meta.sentences;
}

function extractTopic(raw: EnhancedMetaRaw): Topic | null {
  const topic = raw?.topic;
  if (!topic || typeof topic !== 'object') return null;
  const title = typeof topic.title === 'string' ? topic.title.trim() : '';
  const summary = typeof topic.summary === 'string' ? topic.summary.trim() : '';
  if (!title || !summary) return null;
  return { title, summary };
}

export function normalize(raw: EnhancedMetaRaw): Sentence[] {
  const src = Array.isArray(raw?.sentences) ? raw.sentences : [];
  const cleaned = src
    .filter((s) => s && typeof s.start === 'number' && typeof s.end === 'number' && s.end > s.start)
    .map((s) => ({
      start: s.start,
      end: s.end,
      text: (s.text || '').trim(),
      translation: (s.translation || '').trim(),
      grammar: Array.isArray(s.grammar) ? s.grammar : [],
    }))
    .sort((a, b) => a.start - b.start);
  // Trim overlaps: end <= next.start
  for (let i = 0; i < cleaned.length - 1; i++) {
    const cur = cleaned[i]!;
    const next = cleaned[i + 1]!;
    if (cur.end > next.start) cur.end = next.start;
  }
  // Drop any degenerate segments that collapsed to zero-length after trimming
  return cleaned.filter((s) => s.end > s.start);
}

/** Find the index of the sentence containing time t, or -1 if in a gap. */
export function findIndex(sentences: Sentence[], t: number): number {
  let lo = 0;
  let hi = sentences.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const s = sentences[mid]!;
    if (t < s.start) hi = mid - 1;
    else if (t >= s.end) lo = mid + 1;
    else return mid;
  }
  return -1;
}

/** Given current time, return the sentence index for "previous sentence". */
export function prevIndex(sentences: Sentence[], t: number): number {
  if (sentences.length === 0) return -1;
  const i = findIndex(sentences, t);
  if (i !== -1) return Math.max(0, i - 1);
  // In a gap: return the last sentence whose start <= t
  let ans = -1;
  for (let k = 0; k < sentences.length; k++) {
    if (sentences[k]!.start <= t) ans = k;
    else break;
  }
  return ans === -1 ? 0 : ans;
}

/** Given current time, return the sentence index for "next sentence". */
export function nextIndex(sentences: Sentence[], t: number): number {
  if (sentences.length === 0) return -1;
  const i = findIndex(sentences, t);
  if (i !== -1) return Math.min(sentences.length - 1, i + 1);
  const gt = sentences.findIndex((s) => s.start > t);
  return gt === -1 ? sentences.length - 1 : gt;
}
